import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import './Pages.css';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  {
    category: 'Geral',
    question: 'O que é o NutriSync?',
    answer: 'O NutriSync é uma plataforma completa para nutricionistas que combina um painel web profissional com um aplicativo mobile para pacientes. Permite gerenciar pacientes, criar planos alimentares, agendar consultas, gerar relatórios e se comunicar em tempo real com seus pacientes.'
  },
  {
    category: 'Geral',
    question: 'Como funciona o teste grátis?',
    answer: 'Oferecemos 7 dias de teste grátis com acesso completo a todas as funcionalidades. Não é necessário cartão de crédito para começar. Você pode cancelar a qualquer momento durante o período de teste sem nenhum custo.'
  },
  {
    category: 'Geral',
    question: 'Preciso de cartão de crédito para o teste?',
    answer: 'Não! O teste grátis não requer cartão de crédito. Você pode experimentar todas as funcionalidades sem compromisso financeiro.'
  },
  {
    category: 'Geral',
    question: 'Quanto custa o NutriSync?',
    answer: 'Após o período de teste grátis de 7 dias, o NutriSync custa R$ 49,90 por mês. Este valor inclui acesso completo a todas as funcionalidades, suporte técnico e atualizações da plataforma.'
  },
  {
    category: 'Funcionalidades',
    question: 'Quais funcionalidades estão incluídas?',
    answer: 'O NutriSync inclui: dashboard com métricas, gestão completa de pacientes, criação de planos alimentares personalizados, agenda de consultas, chat em tempo real, relatórios detalhados, cálculo nutricional, avaliação nutricional completa e muito mais.'
  },
  {
    category: 'Funcionalidades',
    question: 'Posso criar planos alimentares personalizados?',
    answer: 'Sim! O NutriSync possui um editor visual intuitivo que permite criar planos alimentares semanais personalizados com refeições, horários e observações. Os planos são automaticamente sincronizados com o app mobile dos seus pacientes.'
  },
  {
    category: 'Funcionalidades',
    question: 'Como funciona o chat com pacientes?',
    answer: 'O chat permite comunicação em tempo real entre você e seus pacientes. As mensagens são sincronizadas entre o painel web e o app mobile, permitindo que você responda dúvidas e dê suporte quando necessário.'
  },
  {
    category: 'Funcionalidades',
    question: 'Posso gerar relatórios dos meus pacientes?',
    answer: 'Sim! O NutriSync permite gerar relatórios completos de evolução, adesão ao plano alimentar e resultados. Você pode exportar os relatórios em PDF e compartilhar com seus pacientes.'
  },
  {
    category: 'App Mobile',
    question: 'Meus pacientes precisam pagar pelo app?',
    answer: 'Não! O aplicativo mobile para pacientes é totalmente gratuito. Seus pacientes podem baixar e usar o app sem custo adicional.'
  },
  {
    category: 'App Mobile',
    question: 'O app está disponível para iOS e Android?',
    answer: 'Sim! O aplicativo NutriSync está disponível tanto para dispositivos iOS (iPhone/iPad) quanto para Android. Seus pacientes podem baixar na App Store ou Google Play Store.'
  },
  {
    category: 'App Mobile',
    question: 'Como meus pacientes acessam o app?',
    answer: 'Após você criar a conta do paciente no painel web, eles receberão um convite por e-mail ou SMS com instruções para baixar o app e fazer login. O processo é simples e rápido.'
  },
  {
    category: 'Segurança',
    question: 'Meus dados estão seguros?',
    answer: 'Sim! O NutriSync utiliza criptografia de ponta a ponta, está em conformidade com a LGPD e adota as melhores práticas de segurança da informação. Seus dados e os dados dos seus pacientes são protegidos com medidas técnicas e organizacionais adequadas.'
  },
  {
    category: 'Segurança',
    question: 'O NutriSync é compatível com a LGPD?',
    answer: 'Sim! O NutriSync está totalmente em conformidade com a Lei Geral de Proteção de Dados (LGPD). Temos um Encarregado de Proteção de Dados (DPO) e seguimos todas as diretrizes da legislação.'
  },
  {
    category: 'Pagamento',
    question: 'Quais formas de pagamento são aceitas?',
    answer: 'Aceitamos cartões de crédito (Visa, Mastercard, Elo, Amex) e boleto bancário. O pagamento é processado de forma segura através do Mercado Pago.'
  },
  {
    category: 'Pagamento',
    question: 'Posso cancelar minha assinatura a qualquer momento?',
    answer: 'Sim! Você pode cancelar sua assinatura a qualquer momento através das configurações da sua conta. Não há taxas de cancelamento ou multas.'
  },
  {
    category: 'Pagamento',
    question: 'O que acontece se eu cancelar?',
    answer: 'Ao cancelar, você continuará tendo acesso até o final do período pago. Após isso, sua conta será desativada, mas seus dados serão mantidos por 90 dias caso você queira retornar.'
  },
  {
    category: 'Suporte',
    question: 'Como posso obter suporte?',
    answer: 'Oferecemos suporte através de e-mail, chat na plataforma e central de ajuda. Nossa equipe está disponível para ajudar com qualquer dúvida ou problema técnico.'
  },
  {
    category: 'Suporte',
    question: 'Há limite de pacientes na plataforma?',
    answer: 'Não! Você pode cadastrar quantos pacientes quiser. Não há limite de pacientes, consultas ou planos alimentares na sua assinatura.'
  },
  {
    category: 'Suporte',
    question: 'O NutriSync oferece treinamento?',
    answer: 'Sim! Oferecemos tutoriais em vídeo, documentação completa e suporte para ajudar você a aproveitar ao máximo todas as funcionalidades da plataforma.'
  }
];

export const FAQ: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('Todos');
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());

  const categories = ['Todos', ...Array.from(new Set(faqData.map(item => item.category)))];

  const filteredFAQs = selectedCategory === 'Todos' 
    ? faqData 
    : faqData.filter(item => item.category === selectedCategory);

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  return (
    <div className="ns-legal-page">
      <header className="ns-legal-header">
        <div className="ns-legal-header-content">
          <Link to="/" className="ns-legal-logo">
            <span className="mark">NS</span>
            <span className="text">NutriSync</span>
          </Link>
          <Link to="/">
            <Button variant="secondary">Voltar ao início</Button>
          </Link>
        </div>
      </header>

      <main className="ns-legal-main">
        <div className="ns-legal-container">
          <h1>Perguntas Frequentes (FAQ)</h1>
          <p className="ns-legal-intro">
            Encontre respostas para as dúvidas mais comuns sobre o NutriSync. 
            Se não encontrar o que procura, entre em contato conosco.
          </p>

          <div className="ns-faq-categories">
            {categories.map(category => (
              <button
                key={category}
                className={`ns-faq-category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="ns-faq-list">
            {filteredFAQs.map((item, index) => {
              const globalIndex = faqData.indexOf(item);
              const isOpen = openItems.has(globalIndex);
              
              return (
                <div key={globalIndex} className="ns-faq-item">
                  <button
                    className={`ns-faq-question ${isOpen ? 'open' : ''}`}
                    onClick={() => toggleItem(globalIndex)}
                  >
                    <span>{item.question}</span>
                    <span className="ns-faq-icon">{isOpen ? '−' : '+'}</span>
                  </button>
                  {isOpen && (
                    <div className="ns-faq-answer">
                      <p>{item.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <section className="ns-faq-contact">
            <h2>Ainda tem dúvidas?</h2>
            <p>
              Não encontrou a resposta que procurava? Nossa equipe está pronta para ajudar!
            </p>
            <div className="ns-faq-contact-actions">
              <Link to="/login">
                <Button>Entrar em contato</Button>
              </Link>
              <a href="mailto:suporte@nutrisync.com.br">
                <Button variant="secondary">Enviar e-mail</Button>
              </a>
            </div>
          </section>

          <div className="ns-legal-footer-actions">
            <Link to="/">
              <Button variant="ghost">Voltar ao início</Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

